import { useState } from "react";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * PasswordChangeGate — full-viewport blocker that intercepts any user whose
 * backend row has `must_change_password_on_login === true` (typically freshly
 * broker-onboarded agency clients).
 *
 * Uses a plain fixed overlay at z-[9999] instead of Radix Dialog so it
 * deterministically sits on top of any other modals the app might auto-open
 * on login (e.g. the Rulebook one-pager). No outside-click / Esc dismissal.
 *
 * Mounted once inside AppLayout — so it works on every authenticated route.
 */
export default function PasswordChangeGate() {
    const { user, refresh } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);

    // Only mount for authenticated users with the flag set. `user` may be null
    // (loading) or false (unauthed) — both should render nothing.
    const shouldGate = !!(user && user.must_change_password_on_login);
    if (!shouldGate) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (next !== confirm) {
            toast.error("New password confirmation doesn't match");
            return;
        }
        if (next.length < 8) {
            toast.error("New password must be at least 8 characters");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current, new_password: next,
            });
            toast.success("Password updated. Welcome to Melian Dialogue.");
            // Refresh user so `must_change_password_on_login` clears and the
            // gate unmounts. Fallback: reload if refresh isn't exposed.
            if (typeof refresh === "function") await refresh();
            else window.location.reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Password change failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            data-testid="password-change-gate"
            role="dialog"
            aria-modal="true"
        >
            <div className="w-full max-w-md bg-card border border-border rounded-lg shadow-2xl p-5">
                <h2 className="flex items-center gap-2 text-lg font-bold">
                    <KeyRound className="w-5 h-5 text-primary" />
                    Set your password
                </h2>
                <p className="text-xs text-muted-foreground mt-1 mb-4">
                    Your broker onboarded you with a temporary password. Please
                    choose a permanent one before you continue.
                </p>
                <form className="space-y-3" onSubmit={submit}>
                    <PwField label="Temporary password" value={current} onChange={setCurrent}
                        testId="pw-gate-current" />
                    <PwField label="New password (min 8 characters)" value={next} onChange={setNext}
                        testId="pw-gate-new" />
                    <PwField label="Confirm new password" value={confirm} onChange={setConfirm}
                        testId="pw-gate-confirm" />
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="pw-gate-submit"
                        className="w-full mt-2 py-2 rounded bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60"
                    >
                        {busy ? "Updating…" : "Update password & continue"}
                    </button>
                </form>
            </div>
        </div>
    );
}

function PwField({ label, value, onChange, testId }) {
    return (
        <label className="block">
            <span className="block text-xs font-semibold text-muted-foreground mb-1">{label}</span>
            <input
                type="password"
                required
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded focus:border-primary focus:outline-none"
                autoComplete="new-password"
            />
        </label>
    );
}
